import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonItem,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTitle,
  IonToggle,
  IonToolbar,
  ModalController,
  IonLabel,
} from '@ionic/angular/standalone';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { Store } from '@ngxs/store';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { PuzzleGameModel } from '../../store/puzzle-game.model';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import {
  SetAutoZoomEnabled,
  SetDefinitionBarEnabled,
  SetHighlightRightAndWrongEnabled,
  SetShowErrorsImmediately,
  SetHighContrastEnabled,
  SetDarkMode,
  SetKeepKeyboardOpen,
  SetLetterDeleteRestriction,
  SetLetterOverwriteRestriction,
  SetWordJumpOnRestriction,
  SetWordJumpToRestriction,
} from '../../store/puzzle-game.actions';
import {
  LetterDeleteRestrictions,
  LetterOverwriteRestrictions,
  WordJumpOnRestrictions,
  WordJumpToRestrictions,
  DarkModes,
} from '../../store/puzzle-game-state.model';
import { HelperFunctionsService } from '../helpers/helper-functions.service';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { TranslateModule } from '@ngx-translate/core';
import { PuzzleTypeIdEnum } from '../../puzzle-framework-module/puzzle-type-id.enum';

@Component({
  selector: 'lib-puzzle-settings-modal',
  templateUrl: './puzzle-settings-modal.component.html',
  styleUrls: ['./puzzle-settings-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    IonButton,
    IonCol,
    IonContent,
    IonFooter,
    IonGrid,
    IonHeader,
    IonItem,
    IonList,
    IonRadio,
    IonRadioGroup,
    IonText,
    IonTitle,
    IonToggle,
    IonToolbar,
    IonLabel,
  ],
})
export class PuzzleSettingsModalComponent implements OnInit {
  isCrossword221 = false;
  isCrosswordGame = false;
  isSudokuGame = false;
  isHashiGame = false;
  autoZoomEnabled: boolean;
  definitionBarEnabled: boolean;
  keepKeyboardOpen: boolean;

  letterDeleteRestriction: LetterDeleteRestrictions;
  oldLetterDeleteRestriction: LetterDeleteRestrictions;
  letterDeleteRestrictionValues = Object.values(LetterDeleteRestrictions);
  LetterDeleteRestrictions = LetterDeleteRestrictions;

  letterOverwriteRestriction: LetterOverwriteRestrictions;
  oldLetterOverwriteRestriction: LetterOverwriteRestrictions;
  letterOverwriteRestrictionValues = Object.values(LetterOverwriteRestrictions);
  LetterOverwriteRestrictions = LetterOverwriteRestrictions;

  wordJumpOnRestriction: WordJumpOnRestrictions;
  oldWordJumpOnRestriction: WordJumpOnRestrictions;
  wordJumpOnRestrictionValues = Object.values(WordJumpOnRestrictions);
  WordJumpOnRestrictions = WordJumpOnRestrictions;

  wordJumpToRestriction: WordJumpToRestrictions;
  oldWordJumpToRestriction: WordJumpToRestrictions;
  wordJumpToRestrictionValues = Object.values(WordJumpToRestrictions);
  WordJumpToRestrictions = WordJumpToRestrictions;

  highlightRightAndWrongEnabled: boolean;
  oldHighlightRightAndWrongEnabled: boolean;

  showErrorsImmediately: boolean;
  oldShowErrorsImmediately: boolean;

  darkMode: DarkModes;
  oldDarkMode: DarkModes;
  darkModeValues = Object.values(DarkModes);
  DarkModes = DarkModes;

  highContrastEnabled: boolean;
  oldHighContrastEnabled: boolean;

  constructor(
    public device: DeviceDetectorService,
    private ionicModal: ModalController,
    public framework: PuzzleFrameworkService,
    public store: Store,
    public helperFunctions: HelperFunctionsService,
    public leaderboard: LeaderboardService,
    private cd: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.framework.puzzleConfigLoaded$.subscribe((puzzleType) => {
      this.isCrossword221 = puzzleType === PuzzleTypeIdEnum.CROSSWORD_221;
      this.cd.detectChanges();
    });
    const puzzleID = await this.store.selectOnce((state) => state.puzzleID).toPromise();
    const puzzleGameState = (await this.store
      .selectOnce(PuzzleGameState.getByID(puzzleID))
      .toPromise()) as PuzzleGameModel;

    this.store
      .select((state) => state.puzzleGameState.autoZoomEnabled)
      .subscribe((enabled) => {
        this.autoZoomEnabled = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.definitionBarEnabled)
      .subscribe((enabled) => {
        this.definitionBarEnabled = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.keepKeyboardOpen)
      .subscribe((enabled) => {
        this.keepKeyboardOpen = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.letterDeleteRestriction)
      .subscribe((skipLetters: LetterDeleteRestrictions) => {
        this.letterDeleteRestriction = skipLetters;
        this.oldLetterDeleteRestriction = skipLetters;
      });

    this.store
      .select((state) => state.puzzleGameState.letterOverwriteRestriction)
      .subscribe((skipLetters: LetterOverwriteRestrictions) => {
        this.letterOverwriteRestriction = skipLetters;
        this.oldLetterOverwriteRestriction = skipLetters;
      });

    this.store
      .select((state) => state.puzzleGameState.wordJumpOnRestriction)
      .subscribe((jumpOn: WordJumpOnRestrictions) => {
        this.wordJumpOnRestriction = jumpOn;
        this.oldWordJumpOnRestriction = jumpOn;
      });

    this.store
      .select((state) => state.puzzleGameState.wordJumpToRestriction)
      .subscribe((jumpTo: WordJumpToRestrictions) => {
        this.wordJumpToRestriction = jumpTo;
        this.oldWordJumpToRestriction = jumpTo;
      });

    this.store
      .select((state) => state.puzzleGameState.highlightRightAndWrongEnabled)
      .subscribe((enabled) => {
        this.highlightRightAndWrongEnabled = enabled;
        this.oldHighlightRightAndWrongEnabled = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.showErrorsImmediately)
      .subscribe((enabled) => {
        this.showErrorsImmediately = enabled;
        this.oldShowErrorsImmediately = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.darkMode)
      .subscribe((darkMode: DarkModes) => {
        this.darkMode = darkMode;
        this.oldDarkMode = darkMode;
      });

    this.store
      .select((state) => state.puzzleGameState.highContrastEnabled)
      .subscribe((enabled: boolean) => {
        this.highContrastEnabled = enabled;
        this.oldHighContrastEnabled = enabled;
      });

    this.isCrosswordGame = puzzleGameState.gameType === PuzzleGameTypesEnum.CrosswordPuzzle;
    this.isSudokuGame = puzzleGameState.gameType === PuzzleGameTypesEnum.SudokuGame;
    this.isHashiGame = puzzleGameState.gameType === PuzzleGameTypesEnum.HashiGame;
  }

  async closeDialog(reset = false) {
    await this.ionicModal.dismiss(reset);
  }

  setAutoZoom($event: CustomEvent) {
    const autoZoomEnabled = $event.detail.checked;
    this.store.dispatch(new SetAutoZoomEnabled({ autoZoomEnabled }));
  }

  setDefinitionBarEnabled($event: CustomEvent) {
    const definitionBarEnabled = $event.detail.checked;
    this.store.dispatch(new SetDefinitionBarEnabled({ definitionBarEnabled }));
  }

  setKeepKeyboardOpen($event: CustomEvent) {
    this.store.dispatch(new SetKeepKeyboardOpen({ keepKeyboardOpen: $event.detail.checked }));
  }

  async changeLetterOverwriteRestriction($event: CustomEvent) {
    if (!$event.detail.value) {
      return;
    }
    const letterOverwriteRestriction = $event.detail.value;
    if (
      letterOverwriteRestriction === LetterOverwriteRestrictions.CorrectLetters ||
      letterOverwriteRestriction === LetterOverwriteRestrictions.CorrectWords
    ) {
      if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
        this.letterOverwriteRestriction = this.oldLetterOverwriteRestriction || LetterOverwriteRestrictions.All;
        this.cd.detectChanges();
        return;
      }
    }
    this.store.dispatch(new SetLetterOverwriteRestriction({ letterOverwriteRestriction }));
  }

  async changeLetterDeleteRestriction($event: CustomEvent) {
    if (!$event.detail.value) {
      return;
    }
    const letterDeleteRestriction = $event.detail.value;
    if (
      letterDeleteRestriction === LetterDeleteRestrictions.CorrectLetters ||
      letterDeleteRestriction === LetterDeleteRestrictions.CorrectWords
    ) {
      if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
        this.letterDeleteRestriction = this.oldLetterDeleteRestriction || LetterDeleteRestrictions.CompletedWords;
        this.cd.detectChanges();
        return;
      }
    }

    this.store.dispatch(new SetLetterDeleteRestriction({ letterDeleteRestriction }));
  }

  async changeWordJumpOnRestriction($event: CustomEvent) {
    if (!$event.detail.value) {
      return;
    }
    const wordJumpOnRestriction = $event.detail.value;
    if (wordJumpOnRestriction === WordJumpOnRestrictions.CorrectWord) {
      if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
        this.wordJumpOnRestriction = this.oldWordJumpOnRestriction || WordJumpOnRestrictions.CompleteWord;
        this.cd.detectChanges();
        return;
      }
    }
    this.store.dispatch(new SetWordJumpOnRestriction({ wordJumpOnRestriction }));
  }

  async changeWordJumpToRestriction($event: CustomEvent) {
    if (!$event.detail.value) {
      return;
    }
    const wordJumpToRestriction = $event.detail.value;
    if (wordJumpToRestriction === WordJumpToRestrictions.IncorrectWord) {
      if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
        this.wordJumpToRestriction = this.oldWordJumpToRestriction || WordJumpToRestrictions.IncompleteWord;
        this.cd.detectChanges();
        return;
      }
    }
    this.store.dispatch(new SetWordJumpToRestriction({ wordJumpToRestriction }));
  }

  async setHighlightRightAndWrongEnabled($event: CustomEvent) {
    const highlightRightAndWrongEnabled = $event.detail.checked;
    if (highlightRightAndWrongEnabled) {
      if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
        this.highlightRightAndWrongEnabled = this.oldHighlightRightAndWrongEnabled;
        this.cd.detectChanges();
        return;
      }
    }

    this.store.dispatch(new SetHighlightRightAndWrongEnabled({ highlightRightAndWrongEnabled }));
  }

  async setShowErrorsImmediately($event: CustomEvent) {
    const showErrorsImmediately = $event.detail.checked;
    if (showErrorsImmediately) {
      if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
        this.showErrorsImmediately = this.oldShowErrorsImmediately;
        this.cd.detectChanges();
        return;
      }
    }

    this.store.dispatch(new SetShowErrorsImmediately({ showErrorsImmediately }));
  }

  async setHighContrastEnabled($event: CustomEvent) {
    const highContrastEnabled = $event.detail.checked;
    this.store.dispatch(new SetHighContrastEnabled({ highContrastEnabled }));
  }

  async changeDarkMode($event: CustomEvent) {
    if (!$event.detail.value) {
      return;
    }
    const darkMode = $event.detail.value;
    this.store.dispatch(new SetDarkMode({ darkMode }));
  }
}
